import { api } from '@/api/client'

/**
 * Capture heartbeat (CLA-1342) — the client says what state its recording is in.
 *
 * The server's stalled-recording detector (CLA-1248) can only see chunk
 * arrival, and four client states produce identical silence: a pause, an
 * upload that has stalled while the mic keeps running, a suspended tab, and
 * iPhone Safari after WebKit killed the mic on screen-lock (CLA-1341). Two of
 * those are fine and two lose the consult. Over 2026-09-06..11 that was 15
 * alerts, 2 real — and the two real ones were the case where the page was
 * ALIVE and could have said "my track is dead" within seconds instead of the
 * server guessing after ten minutes.
 *
 * So: every ~30s while a capture session exists (paused included) and on
 * every transition, POST `{state, track_live, client, captured_ms}`. The
 * server treats a STALE heartbeat as no information, never as "fine" — a
 * client cannot report its own death — so this only ever suppresses a
 * known-benign state (pause) or accelerates a known-bad one (dead track).
 *
 * `captured_ms` must be a MEASUREMENT of audio captured, not the wall-clock
 * timer. The server takes `max(its own chunk window, captured_ms)` to decide
 * whether a live client has stalled, so a value that keeps rising while the
 * recorder is silent would mask the exact failure this exists to surface. It
 * is therefore accumulated from `ondataavailable` events that carried bytes:
 * time between data events, capped, so a gap with no data counts nothing.
 *
 * `silent_ms` (CLA-1564) is the recording's current continuous silence, from
 * the same clock that drives the 30/60-minute silence warning — 0 while there
 * is sound, omitted when this browser cannot measure it. The server uses it to
 * tell the clinician's OTHER devices about a recording that has gone quiet,
 * and hands the same list back to this one (`onResponse`) for the run-on
 * banner.
 *
 * Failures are swallowed. Nothing about the recording may hinge on this.
 */

export type CaptureState = 'recording' | 'paused' | 'stopping'
export type CaptureClient = 'web_desktop' | 'web_ios' | 'web_android'

export const HEARTBEAT_INTERVAL_MS = 30_000

/**
 * Which class of browser this is — the alert reads it to decide what the
 * silence means, because an iPhone browser and a desktop browser fail in
 * different ways (CLA-1341). iPadOS Safari reports itself as a Mac; the touch
 * points give it away.
 */
export function detectCaptureClient(nav: Navigator = navigator): CaptureClient {
  const ua = nav.userAgent ?? ''
  if (/iPhone|iPad|iPod/.test(ua)) return 'web_ios'
  if (/Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1) return 'web_ios'
  if (/Android/.test(ua)) return 'web_android'
  return 'web_desktop'
}

/**
 * Whether the mic track is still delivering audio. `readyState` goes `ended`
 * when the OS revokes the device; `muted` is what WebKit sets when it
 * suspends capture on screen-lock — the page stays alive, the recorder stays
 * "recording", and nothing else changes. jsdom and older browsers expose
 * neither, in which case we can only say "as far as we know".
 */
export function trackIsLive(stream: MediaStream): boolean {
  const track = stream.getAudioTracks?.()?.[0]
  if (!track) return true
  if (track.readyState && track.readyState !== 'live') return false
  if (track.muted === true) return false
  return true
}

interface CaptureHeartbeatOptions {
  /** Called whenever the track's liveness changes (mute/unmute/ended). */
  onTrackChange?: (live: boolean) => void
  /** Replaceable for tests. */
  post?: (path: string, body: unknown) => Promise<unknown>
  /** Cap on the audio credited to one data event — see `noteData`. */
  maxGapMs?: number
  intervalMs?: number
  /**
   * Current continuous silence in ms (CLA-1564), read at send time. Null or
   * absent: this client is not measuring, so `silent_ms` is left out.
   */
  silentMs?: () => number | null
  /**
   * Each successful response body, for the run-on banner (CLA-1564). A
   * throwing handler is contained here: it must never break the heartbeat.
   */
  onResponse?: (response: unknown) => void
}

export class CaptureHeartbeat {
  private state: CaptureState = 'recording'
  private capturedMs = 0
  private lastDataAt: number | null = null
  private interval: ReturnType<typeof setInterval> | null = null
  private stopped = false
  private inFlight = false
  private pending = false
  /** The latest report's request, settled either way (see `settled`). */
  private lastPost: Promise<unknown> = Promise.resolve()
  private failures = 0
  private readonly client: CaptureClient
  private readonly post: (path: string, body: unknown) => Promise<unknown>
  private readonly maxGapMs: number
  private readonly intervalMs: number
  private readonly unhook: Array<() => void> = []
  private readonly noteId: number
  private readonly stream: MediaStream
  private readonly opts: CaptureHeartbeatOptions

  constructor(noteId: number, stream: MediaStream, opts: CaptureHeartbeatOptions = {}) {
    this.noteId = noteId
    this.stream = stream
    this.opts = opts
    this.client = detectCaptureClient()
    this.post = opts.post ?? ((path, body) => api.post(path, body))
    this.maxGapMs = opts.maxGapMs ?? 10_000
    this.intervalMs = opts.intervalMs ?? HEARTBEAT_INTERVAL_MS
  }

  /** Report `recording`, start the cadence, and watch the track and the tab. */
  start(now: number = Date.now()) {
    this.lastDataAt = now
    this.hookTrack()
    this.hookVisibility()
    this.interval = setInterval(() => void this.beat(), this.intervalMs)
    void this.beat()
  }

  /**
   * A data event with bytes arrived. Credits the time since the previous one
   * (or since recording/resume began), capped so that one large chunk flushed
   * after a long suspension does not claim the whole gap as captured audio —
   * under-reporting is the safe direction, the server has its own window.
   */
  noteData(now: number = Date.now()) {
    if (this.lastDataAt !== null) {
      this.capturedMs += Math.min(Math.max(0, now - this.lastDataAt), this.maxGapMs)
    }
    this.lastDataAt = now
  }

  /** Milliseconds of audio this client believes it has captured. */
  get captured(): number {
    return this.capturedMs
  }

  /** A transition: reports it immediately. */
  setState(state: CaptureState, now: number = Date.now()) {
    if (this.state === state) return
    this.state = state
    // Data does not flow through a pause; credit nothing across it.
    this.lastDataAt = state === 'recording' ? now : null
    void this.beat()
  }

  /** Stop the cadence and detach. Sends nothing — the note itself says how it ended. */
  stop() {
    this.stopped = true
    if (this.interval) clearInterval(this.interval)
    this.interval = null
    this.unhook.forEach((fn) => fn())
    this.unhook.length = 0
  }

  /**
   * Resolves once the report in flight, if any, has landed or failed. After
   * `stop()` no new one starts, so this is the last write the heartbeat makes:
   * discarding a recording waits on it before asking the server to forget the
   * capture (CLA-1880), so a late report cannot write capture state back.
   */
  settled(): Promise<void> {
    return this.lastPost.then(() => undefined)
  }

  /**
   * Send one report now. Serialised: a report requested while one is in
   * flight runs once it lands, so a pause reported during a slow periodic
   * beat is not lost — the server only ever sees the LAST report.
   */
  async beat(): Promise<void> {
    if (this.stopped) return
    if (this.inFlight) {
      this.pending = true
      return
    }
    this.inFlight = true
    try {
      const body: Record<string, unknown> = {
        state: this.state,
        track_live: trackIsLive(this.stream),
        client: this.client,
        captured_ms: Math.round(this.capturedMs),
      }
      const silent = this.readSilentMs()
      if (silent !== null) body.silent_ms = silent
      const request = this.post(`/notes/${this.noteId}/capture_heartbeat`, body)
      this.lastPost = request.catch(() => undefined)
      const response = await request
      this.failures = 0
      if (!this.stopped) {
        try {
          this.opts.onResponse?.(response)
        } catch (e) {
          console.warn('[capture-heartbeat] response handler failed', e)
        }
      }
    } catch {
      // Advisory only. Log the first so a dead endpoint is visible in a
      // console, then stay quiet — a consult's worth of 30s errors helps nobody.
      if (this.failures++ === 0) console.warn('[capture-heartbeat] report failed')
    } finally {
      this.inFlight = false
    }
    if (this.pending) {
      this.pending = false
      await this.beat()
    }
  }

  private readSilentMs(): number | null {
    try {
      const v = this.opts.silentMs?.()
      return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : null
    } catch {
      return null
    }
  }

  private hookTrack() {
    const track = this.stream.getAudioTracks?.()?.[0]
    if (!track?.addEventListener) return
    const report = () => {
      this.opts.onTrackChange?.(trackIsLive(this.stream))
      void this.beat()
    }
    for (const ev of ['mute', 'unmute', 'ended'] as const) {
      track.addEventListener(ev, report)
      this.unhook.push(() => track.removeEventListener(ev, report))
    }
  }

  private hookVisibility() {
    if (typeof document === 'undefined' || !document.addEventListener) return
    // Both directions: going hidden is when things go wrong, coming back is
    // the first chance to say whether they did.
    const report = () => void this.beat()
    document.addEventListener('visibilitychange', report)
    this.unhook.push(() => document.removeEventListener('visibilitychange', report))
  }
}
